import { AnyAction, Dispatch } from '@reduxjs/toolkit'
import { MainApi } from 'api/MainApi'
import { Feature, GeoJsonProperties, Geometry, MultiLineString, Point, Polygon } from 'geojson'
import cloneDeep from 'lodash.clonedeep'
import isEqual from 'lodash.isequal'
import { MissionActions } from 'store/actions/mission'
import { bbox } from 'turf'
import {
  MISSION_CALCULATING_STATUSES,
  MISSION_MAP_DRAWING_MODES,
  MISSION_PAGE_MODES,
  MISSION_ZONE_TYPES,
  PROGRESS_TYPES,
  TRANSFER_CALCULATING_STATUSES,
  TRANSFER_TYPE,
} from 'utils/constants'
import {
  ICleaningZone,
  ILaunchPoint,
  IPoint,
  IRoute,
  ITransferRoute,
  IZone,
  IZoneTransferRoute,
  RootState,
} from 'utils/interfaces'

const fetchMission = (missionId: number) => {
  return async (dispatch: Dispatch<AnyAction>, getState: () => RootState) => {
    try {
      dispatch(MissionActions.setLoadingProgress(PROGRESS_TYPES.WORK))
      const mission = await MainApi.fetchMission(missionId)
      dispatch(MissionActions.setMission(mission))
      dispatch(MissionActions.setLoadingProgress(PROGRESS_TYPES.SUCCESS))
    } catch (error) {
      dispatch(MissionActions.setLoadingProgress(PROGRESS_TYPES.ERROR))
    }
  }
}

const fetchMissionBackground = (missionId: number) => {
  return async (dispatch: Dispatch<AnyAction>, getState: () => RootState) => {
    try {
      const mission = await MainApi.fetchMission(missionId)
      dispatch(MissionActions.setMission(mission))
    } catch (error) {
      console.log('Error fetching mission')
    }
  }
}

const fetchCalculationStatus = (missionId: number) => {
  return async (dispatch: Dispatch<AnyAction>, getState: () => RootState) => {
    try {
      const status = await MainApi.fetchCalculationStatus(missionId)
      if (!status) {
        return
      }
      if (status.status !== getState().mission.status) {
        const newMission = await MainApi.fetchMission(getState().mission.missionInfo?.id || 0)
        dispatch(MissionActions.setMission(newMission))
        dispatch(MissionActions.setCalculatingStatus(newMission.status))
      }
      if (status.is_ready_to_start_calculation !== getState().mission.isReadyForCalculation) {
        dispatch(MissionActions.setIsReadyForCalculation(status.is_ready_to_start_calculation))
      }

      // // Если статусы зон поменялись, то надо либо удалить маршруты, либо загрузить их
      // status.cleaning_zones_statuses.forEach(async cleaningZoneNewStatusObject => {
      //   const cleaningZone = getState().mission.cleaningZones.find(
      //     zone => zone.properties?.zone_id === cleaningZoneNewStatusObject.id
      //   )
      //   if (cleaningZone && cleaningZone.properties?.status !== cleaningZoneNewStatusObject.status) {
      //     if (cleaningZoneNewStatusObject.status === MISSION_CALCULATING_STATUSES.OK) {
      //       // Обновляем маршрут зоны
      //       const { route } = await MainApi.fetchCleaningZone(cleaningZone.properties?.zone_id)
      //       dispatch(MissionActions.addRoute(route))
      //     } else if (
      //       cleaningZoneNewStatusObject.status === MISSION_CALCULATING_STATUSES.ERROR ||
      //       cleaningZone.properties?.status === MISSION_CALCULATING_STATUSES.NEW ||
      //       cleaningZone.properties?.status === MISSION_CALCULATING_STATUSES.CALCULATING
      //     ) {
      //       // Удаляем маршрут зоны
      //       dispatch(MissionActions.deleteRoute(cleaningZone.properties?.zone_id))
      //     }
      //   }
      // })
      dispatch(MissionActions.setZonesStatus(status.cleaning_zones_statuses))
      dispatch(MissionActions.setLayerStatus(status.xyz_layers_statuses))

      return status
    } catch (error) {
      console.log(error)
    }
  }
}

const createCleaningZone = (
  zoneName: string,
  zoneDescription: string,
  zoneGeometry: Polygon,
  routeId: number | undefined,
  launchPointGeometry: Point | undefined,
  cleaningSpeed: number,
  transportSpeed: number,
  is_border_cleaning?: boolean,
  zoneRoute?: MultiLineString | undefined,
  equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean },
  is_recorded?: boolean,
  setup?: {
    swath_sequence_mode?: number
    borders_num?: number
    main_thresholds?: { max_edge_angle?: number; max_edge_angle_for_borders?: number }
  },
  backwardGeometry?: MultiLineString | undefined
) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      dispatch(MissionActions.setSavingZoneProgress(PROGRESS_TYPES.WORK))
      const { zone, launchPoint } = await MainApi.createCleaningZone(
        getState().mission.missionInfo?.id || -1,
        zoneName,
        zoneDescription,
        zoneGeometry,
        routeId,
        launchPointGeometry,
        cleaningSpeed,
        transportSpeed,
        is_border_cleaning,
        zoneRoute,
        equipment_setup,
        is_recorded,
        setup,
        backwardGeometry
      )
      if (!routeId)
        dispatch(MissionActions.setCleaningZones([...getState().mission.cleaningZones, zone as ICleaningZone]))
      dispatch(MissionActions.setCalculatingStatus(MISSION_CALCULATING_STATUSES.NEW))
      dispatch(MissionActions.setTempZone(undefined))
      dispatch(MissionActions.setTempZoneRoute(undefined))
      dispatch(MissionActions.setTempLaunchPoint(undefined))
      dispatch(MissionActions.setMode(MISSION_PAGE_MODES.VIEW_ZONES))
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
      if (launchPoint) {
        dispatch(MissionActions.addLaunchPoint(launchPoint))
      }
      dispatch(MissionActions.setSavingZoneProgress(PROGRESS_TYPES.SUCCESS))
    } catch (error) {
      console.log('Error creating cleaning zone')
      dispatch(MissionActions.setTempZone(undefined))
      dispatch(MissionActions.setSavingZoneProgress(PROGRESS_TYPES.ERROR))
    }
  }
}

const createRestrictZone = (zoneName: string, zoneDescription: string, zoneGeometry: Polygon) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      dispatch(MissionActions.setSavingZoneProgress(PROGRESS_TYPES.WORK))
      const zone = await MainApi.createRestrictZone(
        getState().mission.missionInfo?.id || -1,
        zoneName,
        zoneDescription,
        zoneGeometry
      )
      dispatch(MissionActions.setRestrictZones([...getState().mission.restrictZones, zone]))
      dispatch(MissionActions.deleteRoutes())
      dispatch(MissionActions.setCalculatingStatus(MISSION_CALCULATING_STATUSES.NEW))
      dispatch(MissionActions.setTempZone(undefined))
      dispatch(MissionActions.setMode(MISSION_PAGE_MODES.VIEW_ZONES))
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
      dispatch(MissionActions.setSavingZoneProgress(PROGRESS_TYPES.SUCCESS))
    } catch (error) {
      console.log('Error creating restrict zone')
      dispatch(MissionActions.setTempZone(undefined))
      dispatch(MissionActions.setSavingZoneProgress(PROGRESS_TYPES.ERROR))
    }
  }
}

const editCleaningZone = (
  zoneId: number,
  zoneName: string,
  zoneDescription: string,
  zoneGeometry: Polygon | undefined,
  zoneRoute: MultiLineString | undefined,
  routeId: number | undefined,
  launchPointGeometry: Point | undefined,
  cleaningSpeed?: number,
  transportSpeed?: number,
  is_border_cleaning?: boolean,
  equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean; scrubber_brush_speed?: number },
  setup?: {
    swath_sequence_mode?: number
    borders_num?: number
    main_thresholds?: { max_edge_angle?: number; max_edge_angle_for_borders?: number }
  },
  keepEditing?: boolean,
  backwardGeometry?: MultiLineString | undefined
) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      const geometryChanged = !isEqual(zoneGeometry, getState().mission.editingZone?.geometry)
      const routeGeometryChanged = !isEqual(zoneRoute, getState().mission.editingZoneRoute?.geometry)
      const launchPointChanged = !isEqual(launchPointGeometry, getState().mission.editingLaunchPoint?.geometry)
      dispatch(MissionActions.setSavingZoneProgress(PROGRESS_TYPES.WORK))

      const { zone, launchPoint, cleaning_route_geometry } = await MainApi.patchCleaningZone(
        zoneId,
        getState().mission.missionInfo?.id || -1,
        zoneName,
        zoneDescription,
        routeId,
        geometryChanged ? zoneGeometry : undefined,
        routeGeometryChanged ? zoneRoute : undefined,
        launchPointChanged ? (launchPointGeometry ? launchPointGeometry : null) : undefined,
        cleaningSpeed,
        transportSpeed,
        is_border_cleaning,
        equipment_setup,
        setup,
        routeGeometryChanged ? backwardGeometry : undefined
      )

      dispatch(MissionActions.editCleaningZone(zone as ICleaningZone))
      if (geometryChanged || launchPointChanged || routeId) {
        dispatch(MissionActions.deleteRoute(zone.properties?.zone_id as number))
      }
      dispatch(MissionActions.deleteLaunchPoint(zoneId))
      if (launchPoint) {
        dispatch(MissionActions.addLaunchPoint(launchPoint))
      }
      if (routeGeometryChanged) {
        const finalRoute = cleaning_route_geometry || zoneRoute
        if (finalRoute) {
          dispatch(MissionActions.deleteRoute(zone.properties?.zone_id as number))
          dispatch(MissionActions.addRoute(finalRoute as IRoute))
        }
      }

      if (!keepEditing) {
        dispatch(MissionActions.setEditingZone(undefined))
        dispatch(MissionActions.setEditingZoneRoute(undefined))
        dispatch(MissionActions.setTempZone(undefined))
        dispatch(MissionActions.setTempZoneRoute(undefined))
        dispatch(MissionActions.setTempLaunchPoint(undefined))
        dispatch(MissionActions.setMode(MISSION_PAGE_MODES.VIEW_ZONES))
        dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
      } else {
        dispatch(MissionActions.setEditingZone(zone as ICleaningZone))
        dispatch(MissionActions.setTempZone(zone as ICleaningZone))
      }

      dispatch(MissionActions.setSavingZoneProgress(PROGRESS_TYPES.SUCCESS))
      const missionId = getState().mission.missionInfo?.id
      if (missionId) {
        dispatch(fetchMissionBackground(missionId))
      }
    } catch (error) {
      console.log('Error editing cleaning zone')
      if (!keepEditing) {
        dispatch(MissionActions.setEditingZone(undefined))
      }
      dispatch(MissionActions.setSavingZoneProgress(PROGRESS_TYPES.ERROR))
    }
  }
}

const editRestrictZone = (zoneId: number, zoneName: string, zoneDescription: string, zoneGeometry: Polygon) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      dispatch(MissionActions.setSavingZoneProgress(PROGRESS_TYPES.WORK))
      const geometryChanged = !isEqual(zoneGeometry, getState().mission.editingZone?.geometry)
      const zone = await MainApi.patchRestrictZone(
        zoneId,
        getState().mission.missionInfo?.id || -1,
        zoneName,
        zoneDescription,
        geometryChanged ? zoneGeometry : undefined
      )
      dispatch(MissionActions.editRestrictZone(zone))
      dispatch(MissionActions.setEditingZone(undefined))
      dispatch(MissionActions.setTempZone(undefined))
      dispatch(MissionActions.setMode(MISSION_PAGE_MODES.VIEW_ZONES))
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
      dispatch(MissionActions.setSavingZoneProgress(PROGRESS_TYPES.SUCCESS))
    } catch (error) {
      console.log('Error editing restrict zone')
      dispatch(MissionActions.setEditingZone(undefined))
      dispatch(MissionActions.setSavingZoneProgress(PROGRESS_TYPES.ERROR))
    }
  }
}

const copyCleaningZoneToMission = (zone: IZone, missionId: number) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      const newZone = await MainApi.copyCleaningZoneToMission(zone.properties?.zone_id as number, missionId as number)
      return newZone
    } catch (error) {
      console.log('Error copying zone')
      throw error
    }
  }
}

const moveZone = (zone: IZone, up: boolean) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      await MainApi.moveZone(zone.properties?.zone_id as number, up)
      dispatch(fetchMissionBackground(getState().mission.missionInfo?.id as number))
    } catch (error) {
      console.log('Error moving zone')
      throw error
    }
  }
}

const deleteCleaningZone = (zone: IZone) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      await MainApi.deleteCleaningZone(zone.properties?.zone_id as number)
      const newZones = getState().mission.cleaningZones.filter(zone_ => zone_.id !== zone.id)
      dispatch(MissionActions.setCleaningZones(newZones))
      dispatch(MissionActions.setCalculatingStatus(MISSION_CALCULATING_STATUSES.NEW))
      dispatch(MissionActions.deleteRoute(zone.properties?.zone_id))
      dispatch(MissionActions.deleteLaunchPoint(zone.properties?.zone_id))
      const missionId = getState().mission.missionInfo?.id
      if (missionId) {
        dispatch(fetchMissionBackground(missionId))
      }
    } catch (error) {
      console.log('Error deleting zone')
    }
  }
}

const setOccupiedSide = (zoneId: number, side: 'left' | 'right') => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      await MainApi.setOccupiedSide(zoneId, side)
      const missionId = getState().mission.missionInfo?.id
      if (missionId) {
        dispatch(fetchMissionBackground(missionId))
      }
    } catch (error) {
      console.log('Error setting occupied side')
    }
  }
}

const deleteRestrictZone = (zone: IZone) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      await MainApi.deleteRestrictZone(zone.properties?.zone_id as number)
      const newZones = getState().mission.restrictZones.filter(zone_ => zone_.id !== zone.id)
      dispatch(MissionActions.setRestrictZones(newZones))
      dispatch(MissionActions.setCalculatingStatus(MISSION_CALCULATING_STATUSES.NEW))
      dispatch(MissionActions.deleteRoutes())
      const missionId = getState().mission.missionInfo?.id
      if (missionId) {
        dispatch(fetchMissionBackground(missionId))
      }
    } catch (error) {
      console.log('Error deleting zone')
    }
  }
}

const startCalculatingRoute = (zoneId: number) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      dispatch(MissionActions.setCalculatingStatus(MISSION_CALCULATING_STATUSES.CALCULATING))
      await MainApi.startCalculatingRoute(zoneId)
    } catch (error) {
      console.log('Error start calculating route')
    }
  }
}

const cancelEditingZone = () => {
  return (dispatch: any, getState: () => RootState) => {
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.VIEW_ZONES))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
    dispatch(MissionActions.setTempZone(undefined))
    dispatch(MissionActions.setTempZoneRoute(undefined))
    dispatch(MissionActions.setEditingZone(undefined))
    dispatch(MissionActions.setTempLaunchPoint(undefined))
    dispatch(MissionActions.setEditingLaunchPoint(undefined))
  }
}

const undoRouteEdit = () => {
  return (dispatch: any, getState: () => RootState) => {
    const history = getState().mission.tempZoneRouteHistory
    if (history.length === 0) return
    // Restore previous route state
    dispatch(MissionActions.undoTempZoneRoute())
    // Clear the draw control (VIEW_ZONES calls deleteAll) so the restored route
    // gets re-added fresh with the correct previous coordinates
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
    // Re-enter route editing mode in the next tick, after React has rendered
    // the VIEW_ZONES state and DrawControl has called deleteAll
    setTimeout(() => {
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE))
    }, 50)
  }
}

const redoRouteEdit = () => {
  return (dispatch: any, getState: () => RootState) => {
    const future = getState().mission.tempZoneRouteFutureHistory
    if (future.length === 0) return
    // Restore next route state
    dispatch(MissionActions.redoTempZoneRoute())
    // Clear the draw control (VIEW_ZONES calls deleteAll) so the restored route
    // gets re-added fresh with the correct coordinates
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
    // Re-enter route editing mode in the next tick
    setTimeout(() => {
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE))
    }, 50)
  }
}

const undoZoneEdit = () => {
  return (dispatch: any, getState: () => RootState) => {
    const history = getState().mission.tempZoneHistory
    if (history.length === 0) return
    // Restore previous zone state
    dispatch(MissionActions.undoTempZone())
    // Clear the draw control (VIEW_ZONES calls deleteAll) so the restored zone
    // gets re-added fresh with the correct previous coordinates
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
    // Re-enter zone editing mode in the next tick
    setTimeout(() => {
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE))
    }, 50)
  }
}

const redoZoneEdit = () => {
  return (dispatch: any, getState: () => RootState) => {
    const future = getState().mission.tempZoneFutureHistory
    if (future.length === 0) return
    // Restore next zone state
    dispatch(MissionActions.redoTempZone())
    // Clear the draw control (VIEW_ZONES calls deleteAll) so the restored zone
    // gets re-added fresh with the correct coordinates
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
    // Re-enter zone editing mode in the next tick
    setTimeout(() => {
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE))
    }, 50)
  }
}

const createFeature = (evt: { features: Feature[] }) => {
  return async (dispatch: any, getState: () => RootState) => {
    const mapMode = getState().mission.mapMode
    if (mapMode === MISSION_MAP_DRAWING_MODES.DRAW_POLYGON && evt.features[0]) {
      ;(evt.features[0] as Feature<Geometry, GeoJsonProperties>).properties = { name: 'New zone' }
      dispatch(MissionActions.setTempZone(evt.features[0] as IZone))
      dispatch(MissionActions.setTempLaunchPoint(undefined))
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE))
    } else if (mapMode === MISSION_MAP_DRAWING_MODES.DRAW_ROUTE && evt.features[0]) {
      const drawnRoute = evt.features[0]
      const coordinates =
        drawnRoute.geometry.type === 'LineString'
          ? [drawnRoute.geometry.coordinates]
          : (drawnRoute.geometry as any).coordinates
      const newRoute: IRoute = {
        type: 'Feature',
        id: 'cleaning_route_' + Math.random(),
        geometry: {
          type: 'MultiLineString' as const,
          coordinates,
        },
        properties: {
          zone_id: -1,
          id_on_map: 'cleaning_' + Math.random(),
        },
      }
      const routeBbox = bbox(newRoute)
      const newZone: IZone = {
        type: 'Feature',
        id: Math.random(),
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [routeBbox[0], routeBbox[1]],
              [routeBbox[2], routeBbox[1]],
              [routeBbox[2], routeBbox[3]],
              [routeBbox[0], routeBbox[3]],
              [routeBbox[0], routeBbox[1]],
            ],
          ],
        },
        properties: {
          zone_id: -1,
          id_on_map: 'cleaning_' + Math.random(),
          zone_type: MISSION_ZONE_TYPES.RECORDED,
          description: '',
          name: 'Route',
          cleaning_speed: 0.5,
          transport_speed: 0.7,
          is_border_cleaning: false,
        },
      }
      dispatch(MissionActions.setTempZoneRoute(newRoute))
      dispatch(MissionActions.setTempZone(newZone))
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE))
    } else if (mapMode === MISSION_MAP_DRAWING_MODES.ADD_ZONE_LAUNCH_POINT && evt.features[0]) {
      dispatch(MissionActions.setTempLaunchPoint(evt.features[0] as ILaunchPoint))
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE))
    } else if (mapMode === MISSION_MAP_DRAWING_MODES.ADD_MISSION_START_POINT && evt.features[0]) {
      try {
        const missionId = getState().mission.missionInfo?.id
        if (!missionId) {
          return
        }
        const startPoint = await MainApi.changeMissionStartPoint(missionId, (evt.features[0] as IPoint).geometry)
        if (startPoint) {
          dispatch(MissionActions.setMissionStartPoint(startPoint))
          dispatch(fetchMissionBackground(missionId))
        }
        dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
      } catch (error) {
        console.log('Error creating start point')
      }
    } else if (mapMode === MISSION_MAP_DRAWING_MODES.ADD_MISSION_FINISH_POINT && evt.features[0]) {
      try {
        const missionId = getState().mission.missionInfo?.id
        if (!missionId) {
          return
        }
        const finishPoint = await MainApi.changeMissionFinishPoint(missionId, (evt.features[0] as IPoint).geometry)
        if (finishPoint) {
          dispatch(MissionActions.setMissionFinishPoint(finishPoint))
          dispatch(fetchMissionBackground(missionId))
        }
        dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
      } catch (error) {
        console.log('Error creating finish point')
      }
    }
  }
}

const editFeature = (evt: { features: Feature[] }) => {
  return async (dispatch: any, getState: () => RootState) => {
    const mode = getState().mission.mapMode
    if (mode === MISSION_MAP_DRAWING_MODES.DRAW_POLYGON && evt.features[0]) {
      dispatch(MissionActions.setTempZone(evt.features[0] as IZone))
    } else if (mode === MISSION_MAP_DRAWING_MODES.ADD_ZONE_LAUNCH_POINT && evt.features[0]) {
      dispatch(MissionActions.setTempLaunchPoint(evt.features[0] as ILaunchPoint))
    } else if (mode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE) {
      const currentZone = getState().mission.tempZone
      if (currentZone) {
        dispatch(MissionActions.pushTempZoneHistory(currentZone))
      }
      dispatch(MissionActions.setTempZone(evt.features[0] as IZone))
    } else if (mode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_TRANSFER_ROUTE) {
      dispatch(MissionActions.setTempTransfer(evt.features[0] as IZoneTransferRoute))
    } else if (mode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE) {
      const routeFeature = evt.features[0] as IRoute
      const currentRoute = getState().mission.tempZoneRoute
      if (currentRoute) {
        dispatch(MissionActions.pushTempZoneRouteHistory(currentRoute))
      }
      const updatedRouteFeature = {
        ...routeFeature,
        properties: {
          ...(currentRoute?.properties || {}),
          ...(routeFeature.properties || {}),
        },
      }
      dispatch(MissionActions.setTempZoneRoute(updatedRouteFeature))
      const tempZone = getState().mission.tempZone
      if (tempZone && routeFeature && tempZone.properties?.zone_type === MISSION_ZONE_TYPES.RECORDED) {
        const routeBbox = bbox(routeFeature)
        const updatedZone = {
          ...tempZone,
          geometry: {
            type: 'Polygon' as const,
            coordinates: [
              [
                [routeBbox[0], routeBbox[1]],
                [routeBbox[2], routeBbox[1]],
                [routeBbox[2], routeBbox[3]],
                [routeBbox[0], routeBbox[3]],
                [routeBbox[0], routeBbox[1]],
              ],
            ],
          },
        }
        dispatch(MissionActions.setTempZone(updatedZone))
      }
    } else if (mode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_MISSION_POINT) {
      dispatch(MissionActions.setMissionTempPoint(evt.features[0] as IPoint))
    } else if (mode === MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_LAUNCH_POINT) {
      dispatch(MissionActions.setTempLaunchPoint(evt.features[0] as ILaunchPoint))
    }
  }
}

const setEditingZone = (zone: IZone) => {
  return (dispatch: any, getState: () => RootState) => {
    dispatch(MissionActions.setEditingZone(zone))
    dispatch(MissionActions.setTempZone(cloneDeep(zone)))
    dispatch(MissionActions.setHighlightedFeature(zone))
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.EDIT_ZONE))
    if (zone.properties.zone_type !== MISSION_ZONE_TYPES.RESTRICT) {
      const launchPoint = getState().mission.launchPoints.find(
        launchPoint => launchPoint.properties?.zone_id === zone.properties.zone_id
      )
      dispatch(MissionActions.setEditingLaunchPoint(launchPoint))
      dispatch(MissionActions.setTempLaunchPoint(launchPoint))
      const route = getState().mission.cleaningRoutes.find(
        route_ => route_.properties.zone_id === zone.properties.zone_id
      )
      dispatch(MissionActions.setEditingZoneRoute(route))
    }
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE))
  }
}

const setEditingZoneCoverage = (zone: IZone) => {
  return (dispatch: any, getState: () => RootState) => {
    dispatch(MissionActions.setEditingZone(zone))
    dispatch(MissionActions.setTempZone(cloneDeep(zone)))
    dispatch(MissionActions.setHighlightedFeature(zone))
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.ADJUST_COVERAGE))
    if (zone.properties.zone_type !== MISSION_ZONE_TYPES.RESTRICT) {
      const launchPoint = getState().mission.launchPoints.find(
        launchPoint => launchPoint.properties?.zone_id === zone.properties.zone_id
      )
      dispatch(MissionActions.setEditingLaunchPoint(launchPoint))
      dispatch(MissionActions.setTempLaunchPoint(launchPoint))
      const route = getState().mission.cleaningRoutes.find(
        route_ => route_.properties.zone_id === zone.properties.zone_id
      )
      dispatch(MissionActions.setEditingZoneRoute(route))
    }
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE))
  }
}

const setEditingZoneRoute = (zone: IZone) => {
  return (dispatch: any, getState: () => RootState) => {
    const route = getState().mission.cleaningRoutes.find(
      route_ => route_.properties.zone_id === zone.properties.zone_id
    )
    if (!route) {
      return
    }
    dispatch(MissionActions.setEditingZone(zone))
    dispatch(MissionActions.setTempZone(cloneDeep(zone)))
    dispatch(MissionActions.setEditingZoneRoute(route))
    dispatch(MissionActions.setTempZoneRoute(cloneDeep(route)))
    dispatch(MissionActions.setHighlightedFeature(route))
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.EDIT_ZONE_ROUTE))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE))
  }
}

const setEditingTransfer = (route: ITransferRoute) => {
  return (dispatch: any, getState: () => RootState) => {
    dispatch(MissionActions.setEditingTransfer(route))
    const userGeometryTransfer = cloneDeep(route)
    const tempGeometry = userGeometryTransfer.geometry
    if (userGeometryTransfer.userGeometry && userGeometryTransfer.userGeometry.coordinates?.length) {
      userGeometryTransfer.geometry = cloneDeep(userGeometryTransfer.userGeometry)
    }
    userGeometryTransfer.userGeometry = tempGeometry
    dispatch(MissionActions.setTempTransfer(userGeometryTransfer))
    dispatch(MissionActions.setHighlightedFeature(route))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_TRANSFER_ROUTE))
  }
}

const cancelEditingTransfer = () => {
  return (dispatch: any, getState: () => RootState) => {
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
    dispatch(MissionActions.setEditingTransfer(undefined))
    dispatch(MissionActions.setTempTransfer(undefined))
  }
}

const editTransfer = () => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      const transferId = getState().mission.tempTransfer?.properties.id
      if (transferId) {
        await MainApi.patchTransferRoute(transferId, getState().mission.tempTransfer?.geometry)
      }
      const tempTransfer = cloneDeep(getState().mission.tempTransfer as IZoneTransferRoute)
      if (tempTransfer) {
        tempTransfer.userGeometry = tempTransfer.geometry
        // tempTransfer.geometry = { type: 'LineString', coordinates: [] }
        tempTransfer.properties.status = TRANSFER_CALCULATING_STATUSES.NEW
        dispatch(MissionActions.editTransferRoute(tempTransfer))
      }
      dispatch(MissionActions.setEditingTransfer(undefined))
      dispatch(MissionActions.setTempTransfer(undefined))
      dispatch(MissionActions.setMode(MISSION_PAGE_MODES.VIEW_ZONES))
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
    } catch (error) {
      console.log('Error editing transfer route', error)
      dispatch(MissionActions.setEditingTransfer(undefined))
      dispatch(MissionActions.setMode(MISSION_PAGE_MODES.VIEW_ZONES))
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
    }
  }
}

const deleteTransfer = (transferId: number) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      await MainApi.deleteTransferRoute(transferId)
      const newTransferRoutes = getState().mission.transferRoutes.filter(route_ => route_.properties.id !== transferId)
      dispatch(MissionActions.setTransferRoutes(newTransferRoutes))
      const missionId = getState().mission.missionInfo?.id
      if (missionId) {
        dispatch(fetchMissionBackground(missionId))
      }
    } catch (error) {
      console.log('Error deleting transfer')
    }
  }
}

const deleteStartPoint = () => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      const missionId = getState().mission.missionInfo?.id || 0
      await MainApi.changeMissionStartPoint(missionId, null)
      dispatch(MissionActions.setMissionStartPoint(undefined))
      if (missionId) {
        dispatch(fetchMissionBackground(missionId))
      }
    } catch (error) {
      console.log('Error deleting start point')
    }
  }
}

const deleteFinishPoint = () => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      const missionId = getState().mission.missionInfo?.id || 0
      await MainApi.changeMissionFinishPoint(missionId, null)
      dispatch(MissionActions.setMissionFinishPoint(undefined))
      if (missionId) {
        dispatch(fetchMissionBackground(missionId))
      }
    } catch (error) {
      console.log('Error deleting finish point')
    }
  }
}

const setEditingPoint = (point: IPoint) => {
  return (dispatch: any, getState: () => RootState) => {
    dispatch(MissionActions.setMissionEditingPoint(point))
    dispatch(MissionActions.setMissionTempPoint(cloneDeep(point)))
    dispatch(MissionActions.setHighlightedFeature(point))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_MISSION_POINT))
  }
}

const cancelEditingPoint = () => {
  return (dispatch: any, getState: () => RootState) => {
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
    dispatch(MissionActions.setMissionEditingPoint(undefined))
    dispatch(MissionActions.setMissionTempPoint(undefined))
  }
}

const editPoint = (type: TRANSFER_TYPE, point: IPoint | undefined) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      if (!point) {
        return
      }
      const missionId = getState().mission.missionInfo?.id || 0
      if (type === TRANSFER_TYPE.START) {
        const startPoint = await MainApi.changeMissionStartPoint(missionId, point.geometry)
        dispatch(MissionActions.setMissionStartPoint(startPoint))
      } else {
        const finishPoint = await MainApi.changeMissionFinishPoint(missionId, point.geometry)
        dispatch(MissionActions.setMissionFinishPoint(finishPoint))
      }
      if (missionId) {
        dispatch(fetchMissionBackground(missionId))
      }
    } catch (error) {
      console.log('Error editing cleaning zone')
    }
    dispatch(MissionActions.setMissionTempPoint(undefined))
    dispatch(MissionActions.setMissionEditingPoint(undefined))
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.VIEW_ZONES))
    dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))
  }
}

const getRecordedRoute = (id: number, tempZone: IZone | undefined) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      const route = await MainApi.fetchRecordedRoute(id)
      // clear existing temporary route first
      dispatch(MissionActions.setTempZoneRoute(undefined))
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))

      if (route?.geometry?.coordinates && Array.isArray(route.geometry.coordinates)) {
        setTimeout(() => {
          const newRoute: IRoute = {
            type: 'Feature',
            id: 'cleaning_route_' + Math.random(),
            geometry: route.geometry,
            properties: {
              zone_id: tempZone?.properties.zone_id || -1,
              id_on_map: 'cleaning_' + (tempZone?.properties.zone_id || Math.random()),
            },
          }

          const routeBbox = bbox(route)
          const newZone: IZone = {
            type: 'Feature',
            id: Math.random(),
            geometry: {
              type: 'Polygon',
              coordinates: [
                [
                  [routeBbox[0], routeBbox[1]],
                  [routeBbox[2], routeBbox[1]],
                  [routeBbox[2], routeBbox[3]],
                  [routeBbox[0], routeBbox[3]],
                  [routeBbox[0], routeBbox[1]],
                ],
              ],
            },
            properties: {
              zone_id: tempZone?.properties.zone_id || -1,
              id_on_map: 'cleaning_' + (tempZone?.properties.zone_id || Math.random()),
              zone_type: MISSION_ZONE_TYPES.RECORDED,
              description: tempZone?.properties.description || '',
              name: tempZone?.properties.name || route.properties.name || '',
              route_id: id,
              cleaning_speed: tempZone?.properties.cleaning_speed,
              transport_speed: tempZone?.properties.transport_speed,
              is_border_cleaning: tempZone?.properties.is_border_cleaning,
            },
          }
          dispatch(MissionActions.setTempZoneRoute(newRoute))
          dispatch(MissionActions.setTempZone(cloneDeep(newZone)))
          dispatch(MissionActions.setHighlightedFeature(undefined))
          dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE))
        }, 50) // A small delay is necessary
      } else {
        console.error('Invalid route structure:', route)
      }
    } catch (error) {
      console.log('Error getting route :', id, ', the error:', error)
    }
  }
}

const setCompositeRoute = (ids: number[], tempZone: IZone | undefined) => {
  return async (dispatch: any, getState: () => RootState) => {
    try {
      if (ids.length === 0) {
        dispatch(clearTempRoute())
        return
      }

      // Fetch all routes
      const routes = await Promise.all(ids.map(id => MainApi.fetchRecordedRoute(id)))
      const validRoutes = routes.filter(r => r?.geometry?.coordinates && Array.isArray(r.geometry.coordinates))
      if (validRoutes.length === 0) {
        console.error('No valid routes found')
        return
      }

      // Concatenate coordinates
      const flatCoordinates: number[][] = []
      const allBackwardCoordinates: number[][][] = []
      const allForwardCoordinates: number[][][] = []

      validRoutes.forEach(route => {
        if (route.geometry.type === 'MultiLineString') {
          // Flatten all segments into our single continuous line
          route.geometry.coordinates.forEach((segment: any) => {
            flatCoordinates.push(...segment)
          })
        } else if (route.geometry.type === 'LineString') {
          flatCoordinates.push(...(route.geometry.coordinates as unknown as number[][]))
        }

        if (route.properties.backwardGeometry?.coordinates) {
          route.properties.backwardGeometry.coordinates.forEach((segment: any) => {
            allBackwardCoordinates.push(segment)
          })
        }

        if (route.properties.forwardGeometry?.coordinates) {
          route.properties.forwardGeometry.coordinates.forEach((segment: any) => {
            allForwardCoordinates.push(segment)
          })
        }
      })
      const allCoordinates: number[][][] = [flatCoordinates]

      const lastRoute = validRoutes[validRoutes.length - 1]

      const newRoute: IRoute = {
        type: 'Feature',
        id: 'cleaning_route_' + Math.random(),
        geometry: {
          type: 'MultiLineString',
          coordinates: allCoordinates,
        },
        properties: {
          zone_id: tempZone?.properties.zone_id || -1,
          id_on_map: 'cleaning_' + (tempZone?.properties.zone_id || Math.random()),
          backwardGeometry:
            allBackwardCoordinates.length > 0
              ? {
                  type: 'MultiLineString',
                  coordinates: allBackwardCoordinates,
                }
              : undefined,
          forwardGeometry:
            allForwardCoordinates.length > 0
              ? {
                  type: 'MultiLineString',
                  coordinates: allForwardCoordinates,
                }
              : undefined,
        },
      }

      const routeBbox = bbox(newRoute)
      const newZone: IZone = {
        type: 'Feature',
        id: Math.random(),
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [routeBbox[0], routeBbox[1]],
              [routeBbox[2], routeBbox[1]],
              [routeBbox[2], routeBbox[3]],
              [routeBbox[0], routeBbox[3]],
              [routeBbox[0], routeBbox[1]],
            ],
          ],
        },
        properties: {
          zone_id: tempZone?.properties.zone_id || -1,
          id_on_map: 'cleaning_' + (tempZone?.properties.zone_id || Math.random()),
          zone_type: MISSION_ZONE_TYPES.RECORDED,
          description: tempZone?.properties.description || '',
          name: tempZone?.properties.name || lastRoute.properties.name || '',
          route_id: lastRoute.id as number, // Use the last route ID as the reference
          cleaning_speed: tempZone?.properties.cleaning_speed,
          transport_speed: tempZone?.properties.transport_speed,
          is_border_cleaning: tempZone?.properties.is_border_cleaning,
        },
      }

      dispatch(MissionActions.setTempZoneRoute(undefined))
      dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.VIEW_ZONES))

      setTimeout(() => {
        dispatch(MissionActions.setTempZoneRoute(newRoute))
        dispatch(MissionActions.setTempZone(cloneDeep(newZone)))
        dispatch(MissionActions.setHighlightedFeature(undefined))
        dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE))
      }, 50)
    } catch (error) {
      console.log('Error setting composite route:', error)
    }
  }
}

const clearTempZone = () => {
  return async (dispatch: any, getState: () => RootState) => {
    dispatch(MissionActions.setTempZone(undefined))
    dispatch(MissionActions.setTempZoneRoute(undefined))
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.DRAW_ZONE))
    await dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE)) // needs to clean MapBox temp drawing data
    await dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DIRECT_SELECT_ZONE_ROUTE))
    await dispatch(MissionActions.setMapMode(MISSION_MAP_DRAWING_MODES.DRAW_POLYGON))
  }
}

const updateDrawnRoute = (feature: Feature) => {
  return async (dispatch: any, getState: () => RootState) => {
    const coordinates =
      feature.geometry.type === 'LineString' ? [feature.geometry.coordinates] : (feature.geometry as any).coordinates

    const normalizedCoordinates =
      coordinates[0] && coordinates[0].length === 1 ? [[coordinates[0][0], coordinates[0][0]]] : coordinates

    const currentRoute = getState().mission.tempZoneRoute
    const newRoute: IRoute = {
      type: 'Feature',
      id: feature.id || 'cleaning_route_' + Math.random(),
      geometry: {
        type: 'MultiLineString' as const,
        coordinates: normalizedCoordinates,
      },
      properties: {
        ...(currentRoute?.properties || {}),
        zone_id: currentRoute?.properties?.zone_id ?? -1,
        // Preserve id_on_map so the zoom dedup doesn't re-trigger on every click
        id_on_map: currentRoute?.properties?.id_on_map || 'cleaning_' + Math.random(),
      },
    }
    dispatch(MissionActions.setTempZoneRoute(newRoute))

    // Only update tempZone (bbox) when drawing a brand-new route.
    // When editing an existing zone's route, preserve the original zone geometry.
    if (!getState().mission.editingZone) {
      const existingTempZone = getState().mission.tempZone
      const routeBbox = bbox(newRoute)
      const newZone: IZone = {
        type: 'Feature',
        // Preserve existing id so the dedup key stays stable across point additions
        id: existingTempZone?.id ?? Math.random(),
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [routeBbox[0], routeBbox[1]],
              [routeBbox[2], routeBbox[1]],
              [routeBbox[2], routeBbox[3]],
              [routeBbox[0], routeBbox[3]],
              [routeBbox[0], routeBbox[1]],
            ],
          ],
        },
        properties: {
          zone_id: -1,
          // Preserve id_on_map so the zoom dedup doesn't re-trigger on every click
          id_on_map: existingTempZone?.properties?.id_on_map || 'cleaning_' + Math.random(),
          zone_type: MISSION_ZONE_TYPES.RECORDED,
          description: '',
          name: existingTempZone?.properties?.name || 'Route',
          cleaning_speed: existingTempZone?.properties?.cleaning_speed || 0.5,
          transport_speed: existingTempZone?.properties?.transport_speed || 0.7,
          is_border_cleaning: existingTempZone?.properties?.is_border_cleaning || false,
        },
      }
      dispatch(MissionActions.setTempZone(newZone))
    }
  }
}

const clearTempRoute = () => {
  return async (dispatch: any, getState: () => RootState) => {
    dispatch(MissionActions.setTempZoneRoute(undefined))
    dispatch(MissionActions.setTempZone(undefined)) // Needs for not showing the zone polygon and only before route view will be fixed
    dispatch(MissionActions.setMode(MISSION_PAGE_MODES.DRAW_ROUTE))
  }
}

export const missionThunks = {
  fetchMission,
  fetchMissionBackground,
  fetchCalculationStatus,
  createCleaningZone,
  editCleaningZone,
  deleteCleaningZone,
  setOccupiedSide,
  createRestrictZone,
  editRestrictZone,
  deleteRestrictZone,
  startCalculatingRoute,
  cancelEditingZone,
  createFeature,
  editFeature,
  setEditingZone,
  setEditingZoneCoverage,
  setEditingZoneRoute,
  setEditingTransfer,
  cancelEditingTransfer,
  editTransfer,
  deleteTransfer,
  deleteStartPoint,
  deleteFinishPoint,
  setEditingPoint,
  cancelEditingPoint,
  editPoint,
  getRecordedRoute,
  setCompositeRoute,
  clearTempZone,
  clearTempRoute,
  copyCleaningZoneToMission,
  moveZone,
  updateDrawnRoute,
  undoRouteEdit,
  redoRouteEdit,
  undoZoneEdit,
  redoZoneEdit,
}
